import { cva, type VariantProps } from 'class-variance-authority';
import { format } from 'date-fns';
import * as React from 'react';

import { formatStandardDate, parseFlexibleDate } from './format-standard-date';
import { cn } from './lib/utils';

/**
 * Visual tone for the milestone eyebrow + date. Echoes the app's status
 * colour language (blue = active / released, emerald = done / complete) while
 * keeping `neutral` and `muted` for lifecycle endpoints that shouldn't shout.
 */
export type LabeledDateTone = 'neutral' | 'info' | 'success' | 'muted';

const eyebrowVariants = cva(
  'inline-flex items-center gap-1.5 text-[0.625rem] leading-none font-semibold tracking-[0.08em] uppercase',
  {
    variants: {
      tone: {
        neutral: 'text-muted-foreground',
        info: 'text-blue-600 dark:text-blue-400',
        success: 'text-emerald-600 dark:text-emerald-400',
        muted: 'text-muted-foreground/70',
      } satisfies Record<LabeledDateTone, string>,
    },
    defaultVariants: { tone: 'neutral' },
  },
);

/** Value-line colour per tone. Archived (`muted`) recedes and is struck. */
const valueToneClass: Record<LabeledDateTone, string> = {
  neutral: 'text-foreground',
  info: 'text-foreground',
  success: 'text-foreground',
  muted: 'text-muted-foreground line-through decoration-muted-foreground/30',
};

export interface LabeledDateProps
  extends Omit<React.ComponentProps<'div'>, 'children'>, VariantProps<typeof eyebrowVariants> {
  /** Eyebrow label naming the milestone, e.g. "Released". */
  label: string;
  /** The milestone date. Accepts a `Date`, an ISO timestamp, or `YYYY-MM-DD`. */
  date: Date | string | null | undefined;
  /**
   * date-fns format string overriding the value line. Defaults to the app's
   * standard date format ("Mon 28th Jul", year appended outside the current
   * year) — pass this only when a caller genuinely needs a different shape.
   */
  dateFormat?: string;
  /** Rendered in place of the date when it is null / unparseable. */
  emptyText?: string;
  /** Lay the label and date side-by-side instead of stacked. */
  orientation?: 'stacked' | 'inline';
  /** Show the leading tone-coloured status pip. */
  showDot?: boolean;
  /** Injectable "today" for the standard format's current-year check. */
  now?: Date;
}

/**
 * A compact two-tier "eyebrow label + date": a tone-coloured milestone name
 * (with a status pip) above a crisp, tabular-aligned date. Purely
 * presentational — callers decide which label / date / tone to pass, so the
 * same primitive renders "Created", "Released", "Completed", or "Archived".
 */
function LabeledDate({
  label,
  date,
  tone = 'neutral',
  dateFormat,
  emptyText = '—',
  orientation = 'stacked',
  showDot = true,
  now,
  className,
  ...props
}: LabeledDateProps) {
  const safeTone: LabeledDateTone = tone ?? 'neutral';
  const resolved = parseFlexibleDate(date);
  const formatted = resolved
    ? dateFormat
      ? format(resolved, dateFormat)
      : formatStandardDate(resolved, { now })
    : null;

  return (
    <div
      data-slot="labeled-date"
      data-tone={safeTone}
      className={cn(
        'min-w-0',
        orientation === 'stacked' ? 'flex flex-col gap-1' : 'inline-flex items-baseline gap-2',
        className,
      )}
      {...props}
    >
      <span className={cn(eyebrowVariants({ tone: safeTone }))}>
        {showDot ? (
          <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
        ) : null}
        {label}
      </span>
      {formatted ? (
        <time
          dateTime={format(resolved as Date, 'yyyy-MM-dd')}
          className={cn('text-[0.8125rem] tabular-nums', valueToneClass[safeTone])}
        >
          {formatted}
        </time>
      ) : (
        <span className="text-muted-foreground text-[0.8125rem] tabular-nums">{emptyText}</span>
      )}
    </div>
  );
}

export { LabeledDate, eyebrowVariants };
